import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { type SuccessResponse } from '@vestigas/data';

import { queryKeysCompanyAccount } from '../queryKeys';

import { type UpdateCompanyAccountPayload } from './types';
import { updateCompanyAccount } from './updateCompanyAccount';

/**
 * Custom hook for updating a company account with a given companyAccountId using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Account/operation/update_company_account_company_account__company_account_uuid__put
 */
export const useMutationUpdateCompanyAccount = (
  options?: Omit<
    UseMutationOptions<
      SuccessResponse,
      Error,
      {
        companyAccountData: UpdateCompanyAccountPayload;
        companyAccountId: string;
      }
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SuccessResponse,
    Error,
    {
      companyAccountData: UpdateCompanyAccountPayload;
      companyAccountId: UUID;
    }
  >({
    mutationFn: async ({ companyAccountData, companyAccountId }) =>
      updateCompanyAccount({
        companyAccountData,
        companyAccountId,
      }),
    onSuccess(data, { companyAccountId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompanyAccount.getAllAdmin({}),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysCompanyAccount.get(companyAccountId),
      });

      return data;
    },
    ...options,
  });
};
